import React from "react";
import { useState, useEffect } from "react";
import Layout2 from "@/components/Layout/Layout2";
import { CardContent, Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Plus, Eye, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SingleOrderDialog from "@/components/dialog/CBD/single-order-dialog";
import { fetchCbdCustomers } from "@/redux/slice/cbd-slice/cbd-slice";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedCustomer } from "@/redux/slice/take-order-slice/selected-customer-slice";
import { setDeliveryInfo } from "@/redux/slice/take-order-slice/delivery-slice";
import useAuth from "@/hooks/useAuth";
import Paginat from "@/components/lib/paginat";
import { Link, useNavigate } from "react-router-dom";
import hasPermission from "@/hooks/hasPermission";


function CBD() {
  const navigate = useNavigate();
  const [expandedRow, setExpandedRow] = useState(null);
  const [singleOrderDialog, setSingleOrderDialog] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [selectedCustomer, setSelectedcustomer] = useState(null);
  const [selectedOrderCustomer, setSelectedOrderCustomer] = useState(null);

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    name: "",
    postcode_first: "",
    postcode_last: "",
    door_no: "",
    home_number: "",
    mobile: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { customers, customersPagination, getLoading } = useSelector(
    (state) => state.cbd.cbd,
  );

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(
        fetchCbdCustomers({
          restaurant_id: user?.restaurant_id,
          per_page: 10,
          page: currentPage,
          sort_field: "id",
          sort_order: "desc",
          name: searchFilters.name,
          mobile: searchFilters.mobile,
          postcode_first: searchFilters.postcode_first,
          postcode_last: searchFilters.postcode_last,
          door_no: searchFilters.door_no,
        }),
      );
    }
  }, [dispatch, currentPage, user?.restaurant_id, searchFilters]);

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleAddressClick = (customer) => {
    setSelectedcustomer(customer);
    setAddressModalOpen(true);
  };

  const handleOrderClick = (customer, orderNumber) => {
    setSelectedOrderCustomer({
      ...customer,
      selectedOrder: orderNumber,
    });
    setSingleOrderDialog(true);
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  function addNewOrder(customer) {
    const customer_info = {
      id: customer?.id,
      full_name: customer?.customer_name || customer?.full_name || "",
      number: customer?.mobile_number || customer?.number || "",
      email: customer?.email || "",
      active_address: customer?.active_address || null,
    };
    dispatch(setSelectedCustomer(customer_info));
    if (customer_info.active_address) {
      dispatch(setDeliveryInfo(customer_info.active_address));
    }
    navigate("/take-order");
  }


  return (
    <Layout2
      pageName={{ title: "Customer Base Database (CBD)" }}
      paths={[
        { name: "CBD", path: "/cbd/all-customer" },
        { name: "All customer", path: "/cbd/all-customer" },
      ]}
    >
      <div className="container flex flex-col gap-5">
        {/* Search Section */}
        <div className="mt-5">
          <h2 className="text-xl font-semibold text-cwhite2 mb-1">Search</h2>
          <Card className="p-3">
            <div className="grid md:grid-cols-4 grid-cols-1 gap-5">
              <div className="flex gap-5 items-center">
                <div className="gap-1 flex flex-col w-full">
                  Name
                  <Input
                    className="w-full"
                    placeholder="Search by Name"
                    value={searchFilters.name}
                    onChange={(e) => handleSearchChange("name", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <span>Postcode</span>
                <Input
                  placeholder="Search by Postcode"
                  value={searchFilters.postcode_first}
                  onChange={(e) =>
                    handleSearchChange("postcode_first", e.target.value)
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <span>House No</span>
                <Input
                  placeholder="Search by House No"
                  value={searchFilters.door_no}
                  onChange={(e) =>
                    handleSearchChange("door_no", e.target.value)
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <span>Contact</span>
                <div className="flex gap-5 items-center">
                  <Input
                    placeholder="Search by Contact"
                    value={searchFilters.mobile}
                    onChange={(e) =>
                      handleSearchChange("mobile", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Search Button
        <div>
          <Button
            className="bg-cgreen w-full"
            onClick={handleSearch}
            disabled={getLoading}
          >
            {getLoading ? "Searching..." : "Search"}
          </Button>
        </div> */}

        {/* Customer Table */}
        <div>
          <Card className="p-3">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Postcode</TableHead>
                  <TableHead>House No</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead className="text-right">Contact Number</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex justify-center items-center gap-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cgreen"></div>
                        <span>Loading customers...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : customers && customers?.length > 0 ? (
                  customers?.map((customer, index) => (
                    <React.Fragment key={customer?.id}>
                      <TableRow
                        onClick={() => handleRowClick(index)}
                        className={`cursor-pointer ${expandedRow === index
                          ? "!bg-gray-700"
                          : "hover:bg-gray-700"
                          }`}
                      >
                        <TableCell className="font-medium">
                          {customer?.customer_name || "-"}
                        </TableCell>
                        <TableCell>{customer?.post_code || "-"}</TableCell>
                        <TableCell>{customer?.door_no || "-"}</TableCell>
                        <TableCell>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddressClick(customer);
                            }}
                            className="text-cwhite hover:text-cgreen transition-colors flex items-center gap-1"
                          >
                            <MapPin size={16} />
                            View
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          {customer?.mobile_number || "-"}
                        </TableCell>
                      </TableRow>

                      {/* Expanded Row - Order Buttons */}
                      {expandedRow === index && (
                        <TableRow className="bg-gray-800">
                          <TableCell colSpan={6}>
                            <div className="flex gap-4 py-4">
                              {hasPermission("cbd.cbd.add_new_order") && (<Button onClick={() => addNewOrder(customer)} className="text-cwhite2 bg-cgreen hover:bg-cgreen/80">
                                <Plus size={16} className="mr-2" />
                                Add New Order
                              </Button>)}

                              {customer?.orders?.map((order, idx) => (
                                <Button
                                  key={order?.id}
                                  onClick={() =>
                                    handleOrderClick(customer, order.id)
                                  }
                                  className="flex-1 text-cwhite2 bg-cblue hover:bg-cblue/80"
                                >
                                  Order -{idx + 1}
                                </Button>
                              ))}

                              {
                                hasPermission("cbd.cbd.view_all_orders") && (
                                  <Link
                                    to={`/cbd/single-customer-details/${customer?.id}`}
                                  >
                                    <Button className="flex-1 text-cwhite2 bg-cpink hover:bg-cpink/80">
                                      View All Orders
                                    </Button>
                                  </Link>
                                )
                              }
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-8 text-cwhite3"
                    >
                      No customers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {customers && customers.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="text-sm text-cwhite3 flex w-full">
                  Page {customersPagination?.current_page} of{" "}
                  {customersPagination?.last_page} | Showing{" "}
                  {customersPagination?.from} to {customersPagination?.to} of{" "}
                  {customersPagination?.total} customers
                </div>
                <Paginat
                  pagination={customersPagination}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Address Modal - Shows all addresses on map */}
      <Dialog open={addressModalOpen} onOpenChange={setAddressModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Addresses for {selectedCustomer?.customer_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {selectedCustomer?.addresses &&
              selectedCustomer?.addresses?.length > 0 ? (
              selectedCustomer?.addresses.map((address, idx) => (
                <Card key={address?.id} className="p-4  text-cwhite ">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">
                        Address {idx + 1}
                        {address?.is_active && (
                          <span className="ml-2 text-xs bg-cgreen text-cblack px-2 py-1 rounded">
                            Active
                          </span>
                        )}
                      </h3>
                      <div className="space-y-1 text-sm text-cwhite3">
                        <p>
                          <span className="font-semibold">Postcode:</span>{" "}
                          {address?.postal_code || address?.post_code}
                        </p>
                        <p>
                          <span className="font-semibold">Door:</span>{" "}
                          {address?.door_no}
                        </p>
                        <p>
                          <span className="font-semibold">City:</span>{" "}
                          {address?.city}
                        </p>
                        <p>
                          <span className="font-semibold">Address:</span>{" "}
                          {address?.address || address?.address_line_1}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <div className="text-center py-8 text-cwhite3">
                No addresses available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Order Dialog */}
      <SingleOrderDialog
        isOpen={singleOrderDialog}
        onClose={() => setSingleOrderDialog(false)}
        customer={selectedOrderCustomer}
      />
    </Layout2>
  );
}

export default CBD;
